import { ColDef, RowNode } from 'ag-grid-community';
import { addYears, startOfYear, subWeeks } from 'date-fns';

import { OfferingFullFilterInput, OfferingStatus } from '../../graphql';
import { CalendarGridOffering } from './components/calendar-grid/CalendarGrid.model';
import { CalendarGridOfferingField } from './components/calendar-grid/CalendarGridColumns';

/**
 * Returns the start date two weeks prior to the current date.
 */
export const previousTwoWeeks = (currentDate: Date) => subWeeks(currentDate, 2);

/**
 * Returns the date one year from the current date
 */
export const yearFromDate = (currentDate: Date) => addYears(currentDate, 1);

/**
 * Returns the year to date from the current date
 */
export const yearToDateStart = (currentDate: Date) => startOfYear(currentDate);

export type CalendarGridRendererProps<
  TOffering extends CalendarGridOffering = CalendarGridOffering
> = {
  data: TOffering;
  value: TOffering[keyof TOffering];
  node?: RowNode;
  /**
   * This comes from a prop on <CalendarGrid />. Hardcoding it is not really a good
   * approach as it's not tied to the source of the data,
   * but at least we now get a typecheck in the renderers.
   */
  context: { matchUrl: string };
};

type ColDefReplacementFields<TOffering extends CalendarGridOffering = CalendarGridOffering> = {
  // The framework-supplied `ColDef` types `field` as string. `keyof` can also be number and symbol.
  // The `& string` part here ensures strict typing, but also makes the framework's ColDef happy.
  field?: CalendarGridOfferingField & string;
  cellRendererFramework?: React.ComponentType<CalendarGridRendererProps<TOffering>>;
  cellRendererParams?: (props: CalendarGridRendererProps<TOffering>) => object;
  equals?: (dataA: TOffering, dataB: TOffering) => boolean;
};

export type ColDefCalendarGrid<TOffering extends CalendarGridOffering = CalendarGridOffering> =
  Omit<ColDef, keyof ColDefReplacementFields> & ColDefReplacementFields<TOffering>;

export type CalendarTabStatus =
  | OfferingStatus.Live
  | OfferingStatus.Priced
  | OfferingStatus.Filed
  | OfferingStatus.Postponed;

export type CalendarTabWhere = OfferingFullFilterInput & { status: { eq: CalendarTabStatus } };
